import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTranslatedUI } from '../hooks/useTranslatedUI';
import { translateBatch } from '../services/translationService';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, TrendingUp, ChevronDown, ChevronUp, Send,
  Share2, Loader2, MessageCircle, ExternalLink, IndianRupee,
  Clock, Sparkles, FileText, Newspaper
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INCOME_STRINGS = {
  loadingTitle: "Generating Your Income Plan",
  loadingText: "Finding the best income opportunities for you...",
  errorGeneric: "Something went wrong. Please try again.",
  goBack: "Go Back",
  backToSchemes: "Back to Schemes",
  searchedFor: "You searched for:",
  reportTitle: "Your Income Intelligence Report",
  topOpportunities: "Top Income Opportunities",
  howToStart: "How to Start",
  totalAnnual: "Total Annual Income Potential",
  whatsapp: "WhatsApp",
  relatedSchemes: "Related Schemes For You",
  viewScheme: "View Scheme",
  marketNews: "Market News",
  askAI: "Ask AI Anything",
  askSubtitle: "Get specific guidance for your situation",
  chatPlaceholder: "Ask about income opportunities, platforms, or next steps...",
  typePlaceholder: "Type your question...",
  thinking: "Thinking...",
};

const PROFILE_LABELS = {
  FARMER: 'Farmer',
  STUDENT: 'Student',
  WOMAN_ENTREPRENEUR: 'Woman Entrepreneur',
  ARTISAN: 'Artisan',
  SMALL_BUSINESS: 'Small Business Owner',
};

const TIMELINE_STYLES = {
  'This week': 'bg-green-100 text-green-700',
  'This month': 'bg-amber-100 text-amber-700',
  'Plan now': 'bg-blue-100 text-blue-700',
};

export default function IncomeIntelligence() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const ui = useTranslatedUI(INCOME_STRINGS);
  const chatEndRef = useRef(null);

  const profileParam = (searchParams.get('profile') || 'farmer').toUpperCase();
  const queryParam = searchParams.get('q') || '';

  const [reportData, setReportData] = useState(null);
  const [translatedReport, setTranslatedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedOpp, setExpandedOpp] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [profileParam, queryParam]);

  // Translate report data when language changes
  useEffect(() => {
    if (!reportData || language === 'en') {
      setTranslatedReport(null);
      return;
    }
    const texts = [];
    const keys = [];
    if (reportData.summary) { texts.push(reportData.summary); keys.push('summary'); }
    if (reportData.total_annual_potential) { texts.push(reportData.total_annual_potential); keys.push('total_annual'); }
    (reportData.metric_cards || []).forEach((m, i) => {
      if (m.label) { texts.push(m.label); keys.push(`mc_label_${i}`); }
      if (m.value) { texts.push(m.value); keys.push(`mc_value_${i}`); }
    });
    (reportData.opportunities || []).forEach((o, i) => {
      if (o.title) { texts.push(o.title); keys.push(`opp_title_${i}`); }
      if (o.description) { texts.push(o.description); keys.push(`opp_desc_${i}`); }
      if (o.income_range) { texts.push(o.income_range); keys.push(`opp_range_${i}`); }
      if (o.timeline) { texts.push(o.timeline); keys.push(`opp_time_${i}`); }
      if (o.platform) { texts.push(o.platform); keys.push(`opp_plat_${i}`); }
      (o.how_to_start || []).forEach((s, j) => { texts.push(s); keys.push(`opp_step_${i}_${j}`); });
    });
    (reportData.income_breakdown || []).forEach((b, i) => {
      if (b.source) { texts.push(b.source); keys.push(`ib_src_${i}`); }
      if (b.amount) { texts.push(b.amount); keys.push(`ib_amt_${i}`); }
    });
    (reportData.related_schemes || []).forEach((s, i) => {
      if (s.name) { texts.push(s.name); keys.push(`rs_name_${i}`); }
      if (s.benefit) { texts.push(s.benefit); keys.push(`rs_ben_${i}`); }
      if (s.reason) { texts.push(s.reason); keys.push(`rs_reason_${i}`); }
    });
    (reportData.suggested_questions || []).forEach((q, i) => {
      texts.push(q); keys.push(`sq_${i}`);
    });

    const doTranslate = async () => {
      const result = {};
      for (let i = 0; i < texts.length; i += 50) {
        try {
          const translated = await translateBatch(texts.slice(i, i + 50), language);
          keys.slice(i, i + 50).forEach((k, idx) => { result[k] = translated[idx]; });
        } catch (e) { console.debug('IncomeIntelligence translate chunk failed; keeping original text', e?.message); }
      }
      setTranslatedReport(result);
    };
    doTranslate();
  }, [reportData, language]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        profile: profileParam,
        q: queryParam,
        language: 'en',
      });
      const res = await fetch(`${API_URL}/api/income-intelligence/report?${params}`);
      if (!res.ok) throw new Error('Failed to load report');
      const data = await res.json();
      if (data.success) {
        setReportData(data);
      } else {
        setError('Failed to generate report');
      }
    } catch (err) {
      console.error('Report error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChat = async () => {
    if (!chatInput.trim()) return;
    const question = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: question }]);
    setChatLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/income-intelligence/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question,
          profile_type: profileParam,
          language: language,
          user_id: user?.id || null,
        }),
      });
      const data = await res.json();
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        text: data.success ? data.response : 'Sorry, please try again.',
      }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', text: 'Connection error. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const handleWhatsAppShare = () => {
    const total = reportData?.total_annual_potential || '₹50,000+';
    const text = `I found out I can earn ${total}/year using KnowMySchemes! See what you can earn: knowmyschemes.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center pb-20 md:pb-0">
          <div className="text-center">
            <div className="relative w-16 h-16 mx-auto mb-5">
              <div className="absolute inset-0 border-4 border-green-200 rounded-full" />
              <div className="absolute inset-0 border-4 border-green-600 rounded-full border-t-transparent animate-spin" />
              <TrendingUp className="absolute inset-0 m-auto w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{ui.loadingTitle}</h3>
            <p className="text-sm text-gray-500">{ui.loadingText}</p>
          </div>
        </main>
      </div>
    );
  }

  if (error || !reportData) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center pb-20 md:pb-0">
          <div className="text-center px-4">
            <p className="text-red-600 mb-4">{error || ui.errorGeneric}</p>
            <Button onClick={() => navigate(-1)} className="bg-green-600 hover:bg-green-700 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> {ui.goBack}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const {
    colors = {}, summary, metric_cards = [], opportunities = [],
    total_annual_potential, income_breakdown = [], related_schemes = [],
    suggested_questions = [], news = [], band_header
  } = reportData;

  const tr = (key, fallback) => (translatedReport && translatedReport[key]) || fallback;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 pb-20 md:pb-0">
        {/* Hero bar */}
        <div style={{ backgroundColor: colors.bg || '#EAF3DE' }}>
          <div className="max-w-5xl mx-auto px-4 py-5 sm:py-6">
            <button
              data-testid="back-from-income"
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-sm mb-3 opacity-70 hover:opacity-100 transition-opacity"
              style={{ color: colors.text }}
            >
              <ArrowLeft className="w-4 h-4" /> {ui.backToSchemes}
            </button>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5" style={{ color: colors.text }} />
              <span
                className="text-[11px] font-bold px-2 py-0.5 rounded-full text-white"
                style={{ backgroundColor: colors.border }}
              >
                {PROFILE_LABELS[profileParam] || 'Income Plan'}
              </span>
            </div>
            <h1
              className="text-xl sm:text-2xl font-bold leading-tight"
              style={{ color: colors.text }}
              data-testid="income-page-title"
            >
              {ui.reportTitle}
            </h1>
            {queryParam && (
              <p className="text-sm mt-1 opacity-80" style={{ color: colors.text }}>
                {ui.searchedFor} "{queryParam}"
              </p>
            )}
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
          {/* Section 1: Summary + Metrics */}
          <section
            className="rounded-2xl border p-5 sm:p-6"
            style={{ backgroundColor: colors.bg, borderColor: colors.border }}
            data-testid="summary-section"
          >
            <p className="text-sm leading-relaxed mb-5" style={{ color: colors.text }}>
              {tr('summary', summary)}
            </p>
            {metric_cards.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {metric_cards.map((card, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 text-center border border-gray-100">
                    <p className="text-[10px] text-gray-500 uppercase tracking-wide font-medium">{tr(`mc_label_${i}`, card.label)}</p>
                    <p className="text-lg font-bold mt-1" style={{ color: colors.text }}>{tr(`mc_value_${i}`, card.value)}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Section 2: Top 3 Income Opportunities */}
          <section data-testid="opportunities-section">
            <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5" style={{ color: colors.text }} />
              {ui.topOpportunities}
            </h2>
            <div className="space-y-3">
              {opportunities.map((opp, i) => {
                const isExpanded = expandedOpp === i;
                const timelineStyle = TIMELINE_STYLES[opp.timeline] || TIMELINE_STYLES['Plan now'];
                return (
                  <div
                    key={i}
                    className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                    data-testid={`opportunity-card-${i}`}
                  >
                    <button
                      className="w-full p-4 sm:p-5 text-left"
                      onClick={() => setExpandedOpp(isExpanded ? null : i)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="text-sm font-semibold text-gray-900">{tr(`opp_title_${i}`, opp.title)}</h3>
                            {opp.timeline && (
                              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${timelineStyle}`}>
                                {tr(`opp_time_${i}`, opp.timeline)}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 leading-relaxed">{tr(`opp_desc_${i}`, opp.description)}</p>
                          <div className="flex items-center gap-3 mt-2">
                            {opp.platform && (
                              <span className="text-[11px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md font-medium">
                                {tr(`opp_plat_${i}`, opp.platform)}
                              </span>
                            )}
                            {opp.income_range && (
                              <span className="text-xs font-bold" style={{ color: colors.text }}>
                                {tr(`opp_range_${i}`, opp.income_range)}
                              </span>
                            )}
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                      </div>
                    </button>
                    {isExpanded && opp.how_to_start?.length > 0 && (
                      <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-gray-100 pt-3">
                        <p className="text-xs font-semibold text-gray-700 mb-2">{ui.howToStart}</p>
                        <ol className="space-y-2">
                          {opp.how_to_start.map((step, j) => (
                            <li key={j} className="flex gap-2.5 text-xs text-gray-600">
                              <span
                                className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                                style={{ backgroundColor: colors.border }}
                              >
                                {j + 1}
                              </span>
                              <span className="pt-0.5">{tr(`opp_step_${i}_${j}`, step)}</span>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Section 3: Income Potential Calculator */}
          {total_annual_potential && (
            <section
              className="rounded-2xl border p-5 sm:p-6"
              style={{ backgroundColor: colors.bg, borderColor: colors.border }}
              data-testid="income-calculator-section"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide" style={{ color: colors.text }}>
                    {ui.totalAnnual}
                  </p>
                  <p className="text-2xl sm:text-3xl font-bold" style={{ color: colors.text }}>
                    {tr('total_annual', total_annual_potential)}
                  </p>
                </div>
                <Button
                  data-testid="whatsapp-share-btn"
                  onClick={handleWhatsAppShare}
                  className="text-white text-xs px-3 py-2 rounded-lg flex items-center gap-1.5"
                  style={{ backgroundColor: colors.text }}
                >
                  <Share2 className="w-3.5 h-3.5" /> {ui.whatsapp}
                </Button>
              </div>
              {income_breakdown.length > 0 && (
                <div className="space-y-2">
                  {income_breakdown.map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-0.5">
                          <span className="text-xs text-gray-700">{tr(`ib_src_${i}`, item.source)}</span>
                          <span className="text-xs font-semibold" style={{ color: colors.text }}>{tr(`ib_amt_${i}`, item.amount)}</span>
                        </div>
                        <div className="w-full bg-white/60 rounded-full h-2">
                          <div
                            className="h-2 rounded-full transition-all"
                            style={{ width: `${Math.min(item.percentage || 0, 100)}%`, backgroundColor: colors.border }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Section 4: Related Schemes */}
          {related_schemes.length > 0 && (
            <section data-testid="related-schemes-section">
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" style={{ color: colors.text }} />
                Related Schemes For You
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {related_schemes.map((scheme, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">{tr(`rs_name_${i}`, scheme.name)}</h3>
                    <p className="text-xs font-medium mb-1.5" style={{ color: colors.text }}>{tr(`rs_ben_${i}`, scheme.benefit)}</p>
                    <p className="text-[11px] text-gray-500">{tr(`rs_reason_${i}`, scheme.reason)}</p>
                    <button
                      onClick={() => navigate('/schemes')}
                      className="mt-3 text-xs font-semibold flex items-center gap-1"
                      style={{ color: colors.text }}
                    >
                      {ui.viewScheme} <ArrowLeft className="w-3 h-3 rotate-180" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Related News */}
          {news.length > 0 && (
            <section data-testid="income-news-section">
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Newspaper className="w-5 h-5" style={{ color: colors.text }} />
                Market News
              </h2>
              <div className="space-y-2">
                {news.map((n, i) => (
                  <a
                    key={i}
                    href={n.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block bg-white rounded-xl border border-gray-200 p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900 line-clamp-2">{n.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{n.source}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Section 5: Ask AI */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="income-chat-section">
            <button
              data-testid="toggle-income-chat"
              onClick={() => setShowChat(!showChat)}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: colors.bg }}>
                  <MessageCircle className="w-5 h-5" style={{ color: colors.text }} />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-gray-900">{ui.askAI}</h2>
                  <p className="text-xs text-gray-500">{ui.askSubtitle}</p>
                </div>
              </div>
              {showChat ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
            </button>

            {showChat && (
              <div className="border-t border-gray-100">
                {/* Suggested questions */}
                {chatMessages.length === 0 && suggested_questions.length > 0 && (
                  <div className="p-3 border-b border-gray-50 flex flex-wrap gap-2">
                    {suggested_questions.map((q, i) => (
                      <button
                        key={i}
                        data-testid={`income-suggested-q-${i}`}
                        onClick={() => { setChatInput(q); setShowChat(true); }}
                        className="text-[11px] border rounded-full px-3 py-1.5 transition-colors"
                        style={{ borderColor: colors.border, color: colors.text, backgroundColor: colors.bg }}
                      >
                        {tr(`sq_${i}`, q)}
                      </button>
                    ))}
                  </div>
                )}

                {/* Messages */}
                <div className="max-h-64 overflow-y-auto p-4 space-y-3">
                  {chatMessages.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-4">{ui.chatPlaceholder}</p>
                  )}
                  {chatMessages.map((msg, i) => (
                    <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                          msg.role === 'user'
                            ? 'text-white rounded-br-sm'
                            : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                        }`}
                        style={msg.role === 'user' ? { backgroundColor: colors.text } : {}}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 rounded-xl px-4 py-2 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" style={{ color: colors.text }} />
                        <span className="text-xs text-gray-500">{ui.thinking}</span>
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Input */}
                <div className="p-3 border-t border-gray-100 flex gap-2">
                  <input
                    data-testid="income-chat-input"
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !chatLoading && handleChat()}
                    placeholder={ui.typePlaceholder}
                    className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:border-transparent"
                    style={{ '--tw-ring-color': colors.border }}
                  />
                  <Button
                    data-testid="income-chat-send"
                    onClick={handleChat}
                    disabled={chatLoading || !chatInput.trim()}
                    className="text-white px-3 py-2 rounded-lg disabled:opacity-50"
                    style={{ backgroundColor: colors.text }}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </section>

          {/* Back button */}
          <div className="text-center pt-4">
            <Button
              data-testid="back-to-schemes-btn"
              onClick={() => navigate('/schemes')}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-xl text-sm font-semibold"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> {ui.backToSchemes}
            </Button>
          </div>
        </div>
      </main>
      <div className="hidden md:block">
        <Footer />
      </div>
    </div>
  );
}
